import React, { useEffect, useMemo, useRef, useState } from "react";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import { normalizeDate } from "../../utilities";
import { Spinner } from "../../components/UI/States";
import {
  ASSIGNABLE_FROM_ANY_BRANCH,
  STATUS_STRIPES,
  STATUS_TONES,
  ageBadge,
  avatarColor,
  chipDotColor,
  fetchLeadLogs,
  initials,
  leadBranch,
  leadStatus,
  titleCase,
} from "./utilities";

// Debounce without pulling in lodash: fires `callback` after `delay` ms of
// silence; pending call is flushed away on unmount.
const useDebounced = (callback, delay = 600) => {
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return (...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => callback(...args), delay);
  };
};

const Avatar = ({ name, size = "w-6 h-6 text-[10px]" }) => (
  <span
    className={`${size} ${avatarColor(
      name
    )} rounded-full inline-flex items-center justify-center font-semibold text-white shrink-0`}
  >
    {initials(name)}
  </span>
);

const MetaChip = ({ label, title }) => (
  <span title={title || label} className="app-chip">
    <span className={`h-2 w-2 shrink-0 rounded-full ${chipDotColor(label)}`} />
    {label}
  </span>
);

// Small uppercase heading over each column of the card body.
const ColumnLabel = ({ children }) => (
  <p className="app-eyebrow mb-2">{children}</p>
);

const TrackedNumberInput = ({ label, value, onCommit }) => {
  const [draft, setDraft] = useState(value || "");
  const commit = useDebounced(onCommit);

  // Keep in sync if another update path changes the lead.
  useEffect(() => setDraft(value || ""), [value]);

  return (
    <label className="inline-flex items-center gap-2 text-xs font-semibold">
      <span className="app-muted whitespace-nowrap">{label}</span>
      <input
        type="text"
        value={draft}
        placeholder="—"
        onChange={(e) => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
        className="app-input h-9 w-28 rounded-lg border px-2.5 text-sm tabular-nums"
      />
    </label>
  );
};

const ContactToggle = ({ label, active, onClick, icon, readOnly }) => {
  const Tag = readOnly ? "span" : "button";
  return (
    <Tag
      type={readOnly ? undefined : "button"}
      onClick={readOnly ? undefined : onClick}
      aria-pressed={readOnly ? undefined : active}
      title={readOnly ? undefined : `Toggle ${label.toLowerCase()} contact`}
      className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
        active
          ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
          : `border-red-500/30 bg-red-500/15 text-red-700 dark:text-red-300 ${
              readOnly ? "" : "hover:bg-red-500/25"
            }`
      }`}
    >
      {icon}
      {label}: {active ? "Contacted" : "Not contacted"}
    </Tag>
  );
};

// Shared look for the person pills in the Assignment column.
const PERSON_PILL =
  "app-surface inline-flex max-w-full items-center gap-2 rounded-full border py-1 pl-1 pr-2 text-sm";

const REMOVE_PERSON_BTN =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-red-600 transition-colors hover:bg-red-500/15 dark:text-red-300";

const SELECT_CLASS = "app-input w-full rounded-xl border px-3 py-2 text-sm";

const LeadCard = ({
  lead,
  tab,
  user,
  permissions,
  managers,
  salesDirectory,
  actions,
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logEntries, setLogEntries] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Slim list payloads only carry a count; the log itself loads on open.
  const logCount = lead.logCount ?? lead.logs?.length ?? 0;
  const openLogs = async () => {
    setLogsOpen(true);
    if (lead.logs || logEntries) return;
    setLogsLoading(true);
    try {
      setLogEntries(await fetchLeadLogs(lead._id));
    } catch (err) {
      console.error("LeadCard logs:", err);
      setLogEntries([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const entry = lead.webleadEntry || {};
  const assignInfo = lead.assignInfo || {};
  const status = leadStatus(lead, tab);
  const age = ageBadge(entry.entryDate);
  const branch = leadBranch(lead);
  const assignedSales = assignInfo.assignedSales || [];
  const isOpenTab = tab === "Active" || tab === "Unassigned";
  const canEdit = permissions.canEditLeads;

  // GM/Area Managers only assign their own team — plus the reps who take
  // leads for any location; Admin/WLM see everyone, grouped by branch with
  // the lead's branch listed first.
  const salesOptions = useMemo(() => {
    const eligible = permissions.canAssignManagers
      ? salesDirectory
      : salesDirectory.filter(
          (p) =>
            permissions.viewableBranches.includes(p.Branch) ||
            ASSIGNABLE_FROM_ANY_BRANCH.includes(p.Name)
        );
    const groups = {};
    eligible.forEach((p) => {
      (groups[p.Branch || "Other"] = groups[p.Branch || "Other"] || []).push(p);
    });
    const branchNames = Object.keys(groups).sort((a, b) => {
      if (a === branch) return -1;
      if (b === branch) return 1;
      return a.localeCompare(b);
    });
    return branchNames.map((name) => ({
      branch: name,
      people: groups[name].sort((a, b) => a.Name.localeCompare(b.Name)),
    }));
  }, [salesDirectory, permissions, branch]);

  const handleAssignSales = (userID) => {
    const person = salesDirectory.find(
      (p) => String(p.UserID) === String(userID)
    );
    if (person) actions.assignSales(lead, person);
  };

  const handleAssignManager = (userID) => {
    const person = managers.find((p) => String(p.UserID) === String(userID));
    if (person) actions.assignManager(lead, person);
  };

  const email = entry.customerEmail;
  const mailHref = `mailto:${email}?subject=${encodeURIComponent(
    `${entry.customerWorkingOn || "Web Lead"} - ${entry.customerName || ""}`
  )}&bcc=webleadtracking@bigclumber.com`;

  return (
    <article className="app-card relative overflow-hidden rounded-2xl border">
      {/* Status accent stripe — clipped to the card's rounded corners. */}
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${STATUS_STRIPES[status.tone]}`}
      />

      {/* ---------------------------------------------------------- header */}
      <div className="app-divider flex flex-wrap items-start justify-between gap-3 border-b py-4 pl-5 pr-4 sm:pl-6">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="min-w-0 truncate text-base font-semibold leading-tight">
              {entry.customerName || "Unknown customer"}
            </h3>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                STATUS_TONES[status.tone]
              }`}
            >
              {status.label}
            </span>
            {age && isOpenTab && (
              <span
                className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium tabular-nums ${
                  STATUS_TONES[age.tone]
                }`}
              >
                {age.label}
              </span>
            )}
          </div>
          <div className="app-muted mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span>Submitted {normalizeDate(entry.entryDate)}</span>
            {branch && (
              <>
                <span aria-hidden="true">·</span>
                <span className="font-medium">{branch}</span>
              </>
            )}
            {entry.type && (
              <>
                <span aria-hidden="true">·</span>
                <span>{entry.type} form</span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openLogs}
            disabled={!logCount}
            title={logCount ? "View activity log" : "No activity yet"}
            aria-label={
              logCount ? `View activity log (${logCount})` : "No activity yet"
            }
            className="app-btn app-btn-ghost app-btn-sm app-muted tabular-nums"
          >
            <HistoryIcon sx={{ fontSize: 18 }} />
            {logCount}
          </button>

          {!canEdit ? null : isOpenTab ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (confirmDelete) {
                    setConfirmDelete(false);
                    actions.remove(lead);
                  } else setConfirmDelete(true);
                }}
                className={`app-btn ${
                  confirmDelete
                    ? "app-btn-danger"
                    : "app-btn-ghost text-red-600 hover:!bg-red-500/10 dark:text-red-300"
                }`}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                {confirmDelete ? "Confirm?" : "Delete"}
              </button>
              <button
                type="button"
                onClick={() => actions.complete(lead)}
                className="app-btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500"
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> Complete
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => actions.restore(lead, tab)}
              className="app-btn app-btn-secondary"
            >
              <RestoreIcon sx={{ fontSize: 18 }} /> Restore
            </button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------ body */}
      <div className="grid grid-cols-1 gap-5 py-4 pl-5 pr-4 sm:pl-6 md:grid-cols-2 lg:grid-cols-12">
        {/* Contact */}
        <div className="min-w-0 lg:col-span-3">
          <ColumnLabel>Contact</ColumnLabel>
          <div className="space-y-2 text-sm">
            {email && (
              <a
                href={mailHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  // Marketing reviews leads without owning them; don't let
                  // their opens count as customer contact.
                  if (canEdit && user.name !== "Alec Evans" && isOpenTab) {
                    actions.setContact(lead, "emailContact", true);
                  }
                }}
                title="Send email (BCCs weblead tracking)"
                className="flex min-w-0 items-center gap-2 transition-colors hover:text-[color:var(--app-accent)]"
              >
                <EmailOutlinedIcon sx={{ fontSize: 18 }} className="app-muted shrink-0" />
                <span className="truncate">{email}</span>
              </a>
            )}
            <div className="flex items-center gap-2">
              <PhoneIcon sx={{ fontSize: 18 }} className="app-muted shrink-0" />
              {entry.customerPhone ? (
                <a
                  href={`tel:${entry.customerPhone}`}
                  className="tabular-nums transition-colors hover:text-[color:var(--app-accent)]"
                >
                  {entry.customerPhone}
                </a>
              ) : (
                <span className="app-muted">None provided</span>
              )}
            </div>
            {entry.customerAddress && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  entry.customerAddress
                )}`}
                target="_blank"
                rel="noreferrer"
                title="View on Google Maps"
                className="flex items-start gap-2 transition-colors hover:text-[color:var(--app-accent)]"
              >
                <PlaceOutlinedIcon sx={{ fontSize: 18 }} className="app-muted mt-0.5 shrink-0" />
                <span>{entry.customerAddress}</span>
              </a>
            )}
          </div>
        </div>

        {/* Request */}
        <div className="min-w-0 lg:col-span-5">
          <ColumnLabel>Looking for</ColumnLabel>
          <p
            className={`max-h-28 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed ${
              entry.customerLookingFor ? "" : "app-muted"
            }`}
          >
            {entry.customerLookingFor || "No details provided."}
          </p>
          {(entry.customerWorkingOn || entry.hearAbout) && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {entry.customerWorkingOn && (
                <MetaChip label={entry.customerWorkingOn} title="Working on" />
              )}
              {entry.hearAbout && (
                <MetaChip
                  label={entry.hearAbout}
                  title={
                    entry.hearAbout === "Other" && entry.hearOther
                      ? `Heard about us: ${entry.hearOther}`
                      : "How they heard about us"
                  }
                />
              )}
            </div>
          )}
        </div>

        {/* Assignment */}
        <div className="min-w-0 md:col-span-2 lg:col-span-4">
          <ColumnLabel>Assignment</ColumnLabel>

          {/* Manager */}
          <div className="mb-3">
            <p className="app-muted mb-1.5 text-xs font-medium">Manager</p>
            {assignInfo.managerAssigned && assignInfo.assignedManager?.Name ? (
              <div className={PERSON_PILL}>
                <Avatar name={assignInfo.assignedManager.Name} />
                <span className="truncate font-medium">
                  {titleCase(assignInfo.assignedManager.Name)}
                </span>
                {permissions.canAssignManagers && isOpenTab && (
                  <button
                    type="button"
                    onClick={() =>
                      actions.removeManager(lead, assignInfo.assignedManager)
                    }
                    title="Remove manager"
                    aria-label={`Remove manager ${titleCase(
                      assignInfo.assignedManager.Name
                    )}`}
                    className={REMOVE_PERSON_BTN}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
            ) : permissions.canAssignManagers && isOpenTab ? (
              <select
                value=""
                onChange={(e) => e.target.value && handleAssignManager(e.target.value)}
                aria-label="Assign a manager"
                className={SELECT_CLASS}
              >
                <option value="">Assign a manager…</option>
                {managers.map((mgr) => (
                  <option key={mgr.UserID} value={mgr.UserID}>
                    {titleCase(mgr.Name)} — {mgr.Branch}
                  </option>
                ))}
              </select>
            ) : (
              <p className="app-muted text-sm">Not assigned yet</p>
            )}
          </div>

          {/* Sales */}
          <div>
            <p className="app-muted mb-1.5 text-xs font-medium">Salespeople</p>
            {assignedSales.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {assignedSales.map((person, index) => (
                  <span key={`${person?.UserID}-${index}`} className={PERSON_PILL}>
                    <Avatar name={person?.Name || "?"} />
                    <span className="truncate font-medium">
                      {titleCase(person?.Name || "")}
                    </span>
                    {permissions.canAssignSales && isOpenTab && (
                      <button
                        type="button"
                        onClick={() => actions.removeSales(lead, person)}
                        title="Remove salesperson"
                        aria-label={`Remove salesperson ${titleCase(
                          person?.Name || ""
                        )}`}
                        className={REMOVE_PERSON_BTN}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {/* Server-side, assigning a manager resets assignInfo entirely,
                so sales assignment is blocked until a manager is on the lead. */}
            {permissions.canAssignSales && isOpenTab && !assignInfo.managerAssigned ? (
              <p className="app-muted text-sm italic">
                Assign a manager first — sales picks are cleared when a
                manager is added.
              </p>
            ) : permissions.canAssignSales && isOpenTab ? (
              <select
                value=""
                onChange={(e) => e.target.value && handleAssignSales(e.target.value)}
                aria-label={
                  assignedSales.length ? "Add another salesperson" : "Assign to sales"
                }
                className={SELECT_CLASS}
              >
                <option value="">
                  {assignedSales.length ? "Add another salesperson…" : "Assign to sales…"}
                </option>
                {salesOptions.map((group) => (
                  <optgroup key={group.branch} label={group.branch}>
                    {group.people.map((person) => (
                      <option key={person.UserID} value={person.UserID}>
                        {titleCase(person.Name)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              assignedSales.length === 0 && (
                <p className="app-muted text-sm">No salesperson assigned</p>
              )
            )}
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- footer */}
      <div className="app-surface app-divider flex flex-wrap items-center gap-x-4 gap-y-3 border-t py-3 pl-5 pr-4 sm:pl-6">
        <div className="flex flex-wrap items-center gap-2">
          <ContactToggle
            label="Phone"
            readOnly={!canEdit}
            icon={<PhoneIcon sx={{ fontSize: 14 }} />}
            active={Boolean(lead.leadProgress?.contactStatus?.phoneContact?.status)}
            onClick={() =>
              actions.setContact(
                lead,
                "phoneContact",
                !lead.leadProgress?.contactStatus?.phoneContact?.status
              )
            }
          />
          <ContactToggle
            label="Email"
            readOnly={!canEdit}
            icon={<EmailOutlinedIcon sx={{ fontSize: 14 }} />}
            active={Boolean(lead.leadProgress?.contactStatus?.emailContact?.status)}
            onClick={() =>
              actions.setContact(
                lead,
                "emailContact",
                !lead.leadProgress?.contactStatus?.emailContact?.status
              )
            }
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {canEdit ? (
            <>
              <TrackedNumberInput
                label="Quote #"
                value={lead.saleData?.quoteNumber}
                onCommit={(value) => actions.setSaleNumber(lead, "quote", value)}
              />
              <TrackedNumberInput
                label="Order #"
                value={lead.saleData?.orderNumber}
                onCommit={(value) => actions.setSaleNumber(lead, "order", value)}
              />
            </>
          ) : (
            <>
              <span className="text-xs font-semibold">
                <span className="app-muted">Quote #</span>{" "}
                <span className="tabular-nums">{lead.saleData?.quoteNumber || "—"}</span>
              </span>
              <span className="text-xs font-semibold">
                <span className="app-muted">Order #</span>{" "}
                <span className="tabular-nums">{lead.saleData?.orderNumber || "—"}</span>
              </span>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setNotesOpen((open) => !open)}
          aria-expanded={notesOpen}
          className={`app-btn app-btn-ghost app-btn-sm ml-auto ${
            notesOpen ? "app-accent" : "app-muted"
          }`}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
          Notes ({lead.notes?.length || 0})
        </button>
      </div>

      {/* ----------------------------------------------------------- notes */}
      {notesOpen && (
        <div className="app-divider border-t py-4 pl-5 pr-4 sm:pl-6">
          <div className="max-h-56 space-y-2 overflow-y-auto">
            {lead.notes?.length ? (
              lead.notes.map((note, index) => (
                <div key={index} className="app-surface flex gap-3 rounded-xl border p-3">
                  <Avatar name={note.username || "?"} size="w-7 h-7 text-[11px]" />
                  <div className="min-w-0">
                    <p className="app-muted text-xs font-medium">
                      {note.username} · {normalizeDate(note.note_date)}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm">{note.note}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="app-muted text-sm">No notes yet.</p>
            )}
          </div>
          {canEdit && (
          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!noteDraft.trim()) return;
              actions.addNote(lead, noteDraft.trim());
              setNoteDraft("");
            }}
          >
            <input
              type="text"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Add a note…"
              aria-label="Add a note"
              className="app-input h-10 min-w-0 flex-1 rounded-xl border px-3 text-sm"
            />
            <button
              type="submit"
              disabled={!noteDraft.trim()}
              className="app-btn app-btn-primary"
            >
              Add
            </button>
          </form>
          )}
        </div>
      )}

      {/* ------------------------------------------------------ logs modal */}
      {logsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--app-scrim)] p-4"
          onClick={() => setLogsOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Activity — ${entry.customerName || "Lead"}`}
            className="app-glass app-glass-rim w-full max-w-lg overflow-hidden rounded-3xl border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="app-divider flex items-center justify-between gap-3 border-b px-5 py-4">
              <h4 className="min-w-0 truncate text-base font-semibold">
                Activity — {entry.customerName || "Lead"}
              </h4>
              <button
                type="button"
                onClick={() => setLogsOpen(false)}
                aria-label="Close activity log"
                className="app-icon-btn"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="max-h-96 space-y-2 overflow-y-auto p-5">
              {logsLoading ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <Spinner size={22} />
                  <p className="app-muted text-sm">Loading activity…</p>
                </div>
              ) : (
                (lead.logs || logEntries || []).map((log, index) => (
                  <div key={index} className="app-surface rounded-xl border p-3 text-sm">
                    <p className="app-muted text-xs font-medium">
                      {normalizeDate(log.timestamp)}
                    </p>
                    <p className="mt-0.5">{log.message}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default LeadCard;
